import React, { useEffect, useMemo, useState } from "react";
import { CSpinner } from "@coreui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/heatmap";

import { apiClient } from "services/Api";

const MONTH_NAMES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

export const CURRENT_YEAR = new Date().getFullYear();
export const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + 1 - i);

const formatPercent = (value) => `${Math.round(value || 0)}%`;

const buildHeatmapOptions = (people) => {
  const categories = people.map((person) => person.name);
  const data = [];
  people.forEach((person, personIndex) => {
    person.eti.forEach((value, monthIndex) => {
      data.push([monthIndex, personIndex, Number(((value || 0) * 100).toFixed(2))]);
    });
  });

  const maxValue = data.reduce((max, [, , value]) => Math.max(max, value), 100);

  return {
    chart: { type: "heatmap" },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: MONTH_NAMES,
      opposite: true,
    },
    yAxis: {
      categories,
      title: undefined,
      reversed: true,
    },
    colorAxis: {
      min: 0,
      max: maxValue,
      stops: [
        [0, "#eef4fb"],
        [Math.min(1, 100 / maxValue), "#2caffe"],
        [1, "#e6002e"],
      ],
      labels: { format: "{value}%" },
    },
    legend: {
      align: "right",
      layout: "vertical",
      verticalAlign: "middle",
      title: { text: "Carga (%)" },
    },
    tooltip: {
      formatter: function () {
        const personName = this.series.yAxis.categories[this.point.y];
        const month = MONTH_NAMES[this.point.x];
        return `<b>${personName}</b><br/>${month}: <b>${formatPercent(this.point.value)}</b>`;
      },
    },
    series: [
      {
        name: "Carga",
        borderWidth: 1,
        borderColor: "#ffffff",
        data,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return this.point.value ? formatPercent(this.point.value) : "";
          },
        },
      },
    ],
  };
};

const PersonWorkloadHeatmap = ({ clientId, noIncentive, year }) => {
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/projects/management/${clientId}/workload/`, {
        params: { year, no_incentive: noIncentive },
      })
      .then((response) => {
        if (cancelled) return;
        setPeople(response.data?.people || []);
      })
      .catch((error) => {
        console.error("Erro ao carregar carga de trabalho:", error);
        if (!cancelled) setPeople([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, noIncentive, year]);

  const chartOptions = useMemo(() => buildHeatmapOptions(people), [people]);
  const chartHeight = Math.max(300, people.length * 32 + 150);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <CSpinner />
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="text-muted text-center py-4">
        Sem dados para apresentar.
      </div>
    );
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={{
        ...chartOptions,
        chart: { ...chartOptions.chart, height: chartHeight },
      }}
    />
  );
};

export default PersonWorkloadHeatmap;
